import { useMemo } from 'react';
import { LabelItem } from '../types/LabelItem';
import { UserWithBarcode } from '../../../application/types/userWithBarcode';
import { StorageLocation } from '../../../application/types/storageLocation';

/**
 * Hook to convert UserWithBarcode[] to LabelItem[] for label printing.
 *
 * Transforms user data into the format needed for label generation.
 * QR code contains only the user's barcode string.
 */
export function useUserLabelGeneration(users: UserWithBarcode[]): LabelItem[] {
    return useMemo(() => {
        return users
            .filter((user) => user.barcode) // Only include users with barcodes
            .map((user) => ({
                id: user.id,
                type: 'user' as const,
                displayName: user.userName,
                qrData: user.barcode, // Just the barcode string
            }));
    }, [users]);
}

/**
 * Hook to convert StorageLocation[] to LabelItem[] for label printing.
 *
 * Transforms storage location data into the format needed for label generation.
 * QR code contains only the storage location's barcode string.
 */
export function useStorageLabelGeneration(
    locations: StorageLocation[]
): LabelItem[] {
    return useMemo(() => {
        return locations
            .filter((location) => location.barCode) // Only include locations with barcodes
            .map((location) => ({
                id: location.id,
                type: 'storage' as const,
                displayName: location.name,
                qrData: location.barCode!, // Just the barcode string
            }));
    }, [locations]);
}
